/**
 * Typed `window.dataLayer` access — the ONLY way feature code talks to GTM.
 *
 * Two shapes go into the dataLayer and they are not interchangeable:
 *
 *   1. **Events** — plain objects `{ event: '…', …props }`. GTM matches these
 *      with a Custom Event trigger. `pushDataLayerEvent` handles these.
 *   2. **gtag commands** — an `arguments` object, NOT an array. GTM silently
 *      ignores `consent`/`set` commands pushed as arrays, which is the single
 *      most common Consent Mode misconfiguration. `gtagCommand` handles these.
 *
 * PostHog is deliberately NOT wired through GTM: it is proxied via `/ingest/*`
 * (see next.config rewrites) precisely so ad blockers can't kill it, and
 * routing it through googletagmanager.com would undo that. GTM here carries
 * Google Ads (and later Meta) conversion tags only.
 */

declare global {
	interface Window {
		dataLayer?: unknown[];
	}
}

/** Every dataLayer event Naumu pushes. Keep this union exhaustive. */
export type DataLayerEventName = 'signup';

/** Props for `{ event: 'signup' }` — the Google Ads conversion trigger. */
export interface SignupDataLayerEvent {
	event: 'signup';
	/** Naumu `User.id` (UUID). Also usable as a GTM user-provided-data key. */
	user_id: string;
	/** Which credential minted the account: `google` | `microsoft` | `password`. */
	method: string;
}

export type DataLayerEvent = SignupDataLayerEvent;

/** Ensure `window.dataLayer` exists and return it. Client-only; null on SSR. */
const getDataLayer = (): unknown[] | null => {
	if (typeof window === 'undefined') return null;
	window.dataLayer = window.dataLayer ?? [];
	return window.dataLayer;
};

/**
 * Push a typed event object. Safe to call before (or without) the container
 * loading — GTM replays whatever is already in the array on boot, and if the
 * container never loads the array is just an unread list.
 */
export const pushDataLayerEvent = (event: DataLayerEvent): void => {
	getDataLayer()?.push(event);
};

/**
 * Push a gtag command as an `arguments` object.
 *
 * The classic-function + `arguments` dance is load-bearing: gtag.js reads
 * `arguments` off the pushed value, and an array literal does not satisfy it.
 */
// eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-unused-vars
export function gtagCommand(..._args: unknown[]) {
	const layer = getDataLayer();
	if (!layer) return;
	// eslint-disable-next-line prefer-rest-params
	layer.push(arguments);
}

/**
 * Announce a completed signup so a GTM Custom Event trigger (`event equals
 * signup`) can fire the Google Ads conversion.
 *
 * Called once per new account from `SignupAttributionReporter`, which owns the
 * sessionStorage de-dupe — this function itself does not guard.
 */
export const pushSignupEvent = (params: { userId: string; method: string }): void => {
	pushDataLayerEvent({
		event: 'signup',
		user_id: params.userId,
		method: params.method,
	});
};
